import React, { useEffect, useRef, useState } from "react";
import "moment/min/locales";
import { Elements, ElementsConsumer } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { HeadProvider, Title, Meta, Link } from "react-head";
import _ from "lodash";
import CookieConsent from "react-cookie-consent";
import MessageModal from "./components/MessageModal";
import Cart from "./components/Cart";
import PaymentModal from "./components/PaymentModal";
import { updateShop } from "./redux/actions/shopActions";
import { fetchConfiguration } from "./redux/actions/configurationAction";
import { listenerAuth, userConnectionCheck } from "./redux/actions/userAction";
import { initializeMessenger } from "./api/messenger";
import { initializeFirebase } from "./api/firebase/firebaseInit";
import { getShopsData } from "./utils/shop";
import {
  BLACK,
  COLOR_LIGHT_GRAY,
  DARK,
  ORDER_TYPE_CLICK_AND_COLLECT,
  ORDER_TYPE_DELIVERY,
  UNIT,
  WHITE,
} from "./constants";
import {
  checkAccessContent,
  getMetadata,
  interceptConsole,
  isQrCode,
  isSplash,
  isWebView,
  isStringNotNull,
} from "./utils";
import {
  isCustomerAddressDefined,
  updateUserAddressAndOrderType,
} from "./utils/customer-address-and-shops";

import ConnexionModal from "./components/ConnexionModal";
import "./css/App.css";
import "./css/react-phone-input.css";
import "./css/App-dark.css";
import "@dishopsaas/design-system/design-system.css";
import { AppThemeProvider } from "@dishopsaas/design-system";
import ScrollToTop from "./components/ScrollToTop";
import { useAppDispatch, useAppSelector } from "./redux/hook";
import { showAddressModal } from "./redux/actions/componentAction";
import { getCookieConsentValue } from "react-cookie-consent";
import AddressModal from "./components/AddressModal";
import AppRoutes from "./routes";
import { verifyPendingOrderValidity } from "./Pages/Home/Home.services";
export default function App() {
  const [stripe, setStripe] = useState(false);
  const [hasCookieConsent, setHasCookieConsent] = useState(
    getCookieConsentValue() === "true",
  );
  const dispatch = useAppDispatch();
  const { shops } = useAppSelector((state) => state.shopReducer);
  const {
    address: customerAddress,
    orderType,
    shopId,
  } = useAppSelector((state) => state.pendingOrderReducer);

  const shopIdFromLink = window.location.pathname.split("/")[1] ?? null;

  const { user, isUserAnonymous } = useAppSelector(
    (state) => state.userReducer,
  );
  const [hasDisplayedOrderTypeDelivery, setHasDisplayedOrderTypeDelivery] =
    useState(false);
  const {
    webapp,
    qrcode,
    name,
    logoUrl,
    theme,
    landing,
    secondaryColor,
    stripeApiKey,
    descriptionText,
  } = useAppSelector((state) => state.configurationReducer);
  const mode = theme === DARK ? DARK : "light";

  useEffect(() => {
    const runInit = async () => {
      interceptConsole();
      const companyId = await initializeFirebase();
      await fetchConfiguration(companyId);
      await userConnectionCheck();
      if (isWebView()) {
        initializeMessenger();
      }
    };

    runInit();
    return () => listenerAuth();
  }, []);

  useEffect(() => {
    checkAccessContent();
  }, [webapp, qrcode]);

  useEffect(() => {
    if (!user?.customerId) return;
    getShopsData();
    if (!shopId) {
      // Lorsqu'on recupere depuis les props il n'a pas encore la valeur definie
      updateShop(null, shopIdFromLink);
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      await verifyPendingOrderValidity();
    })();
  }, []);
  useEffect(() => {
    const handleLoadStripe = async () => {
      const stripeResult = await loadStripe(stripeApiKey);
      setStripe(stripeResult);
    };

    if (stripeApiKey) {
      handleLoadStripe();
    }
  }, [stripeApiKey]);
  useEffect(() => {
    if (hasDisplayedOrderTypeDelivery) {
      return;
    }
    if (orderType === ORDER_TYPE_DELIVERY && customerAddress) {
      setHasDisplayedOrderTypeDelivery(true);
      return;
    }
    const shop = shops?.[shopId];
    if (!shop) {
      return;
    }
    const shopOrderTypes = shop?.configuration?.orderTypes ?? [];
    if (!shopOrderTypes.includes(ORDER_TYPE_DELIVERY)) {
      setHasDisplayedOrderTypeDelivery(true);
      return;
    }
    setHasDisplayedOrderTypeDelivery(true);
    if (!isQrCode() && !isStringNotNull(shopIdFromLink)) {
      dispatch(showAddressModal(true));
    }
  }, [orderType, shopId, shops]);
  const prevCustomerAddress = useRef(null);
  const prevShopId = useRef(null);
  const prevOrderType = useRef(null);
  const prevShops = useRef(null);

  useEffect(() => {
    const customerAddressChanged =
      customerAddress &&
      (!customerAddress ||
        !_.isEqual(
          _.omit(prevCustomerAddress.current ?? {}, ["instruction", "street2"]),
          _.omit(customerAddress, ["instruction", "street2"]),
        ));
    const isSelectedShopChanged =
      (prevShopId.current !== shopId && shopId?.length) ||
      (prevOrderType.current !== orderType && orderType?.length);
    if (
      (customerAddressChanged || isSelectedShopChanged) &&
      isCustomerAddressDefined(customerAddress)
    ) {
      updateShop(customerAddress, shopIdFromLink);
    }

    prevCustomerAddress.current = customerAddress;
    prevOrderType.current = orderType;
    prevShopId.current = shopId;
  }, [customerAddress, shopId, orderType]);

  useEffect(() => {
    if (!_.isEqual(prevShops.current, shops) && !_.isEmpty(shops)) {
      const shopKeys = Object.keys(shops);
      if (shopKeys.length === 1) {
        const shopId = shopKeys[0];
        const shop = shops[shopId];
        const { address, configuration } = shop;
        const { orderTypes } = configuration;
        if (
          orderTypes.includes(ORDER_TYPE_CLICK_AND_COLLECT) &&
          orderTypes.length === 1 &&
          !isQrCode()
        ) {
          updateUserAddressAndOrderType(
            shopId,
            address,
            ORDER_TYPE_CLICK_AND_COLLECT,
          );
        }
      }
    }
    prevShops.current = shops;
  }, [shops]);

  const { title, description, keywords } = getMetadata(
    name,
    webapp,
    descriptionText,
  );
  const landingEnabled = landing?.enabled;
  const singlePageEnabled = isQrCode() || isWebView();

  if (!isWebView() && _.isEmpty(user) && !isUserAnonymous) {
    return <></>;
  }

  return (
    <div id="dishop-order-module-wrapper" className="design-system-wrapper">
      <AppThemeProvider mode={mode} secondaryColor={secondaryColor}>
        <div className={`${theme}-theme`}>
          {name && (
            <HeadProvider>
              <Title>{title}</Title>
              <Meta name="description" content={description} />
              <Meta name="keywords" content={keywords} />
              <Link rel="shortcut icon" href={logoUrl} type="image/x-icon" />
            </HeadProvider>
          )}
          {!singlePageEnabled && !landingEnabled && <ScrollToTop />}
          <AppRoutes
            landingEnabled={landingEnabled}
            singlePageEnabled={singlePageEnabled}
            stripe={stripe}
          />
          <MessageModal />
          <Cart />
          {stripe && (
            <Elements stripe={stripe}>
              <ElementsConsumer>
                {({ elements, stripe }) => (
                  <PaymentModal elements={elements} stripe={stripe} />
                )}
              </ElementsConsumer>
            </Elements>
          )}
          <ConnexionModal />
          {hasCookieConsent && <AddressModal />}
          {!isSplash() && (
            <CookieConsent
              location="bottom"
              expires={365}
              overlay
              buttonText="Tout Accepter"
              buttonStyle={{
                background: secondaryColor || BLACK,
                color: WHITE,
                borderRadius: UNIT / 2,
              }}
              style={{
                backgroundColor: COLOR_LIGHT_GRAY,
                justifyContent: "center",
              }}
              onAccept={() => {
                setHasCookieConsent(true);
                dispatch(showAddressModal(true));
              }}
              overlayStyle={{ zIndex: 3000 }}
            >
              <p className="text-secondary text-justify">
                Nous utilisons des cookies pour offrir des fonctionnalités
                utiles et mesurer les performances afin d&apos;améliorer votre
                expérience.
              </p>
            </CookieConsent>
          )}
        </div>
      </AppThemeProvider>
    </div>
  );
}
